import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TitleCasePipe } from '@angular/common';

import { map, filter } from 'rxjs/operators';

import { GoogleAnalyticsEventsService } from '../services/google-analytics-events.service';

import { SearchService } from './search.service';
import { dateRangeValidator } from './search.validators';
import { Subscription } from 'rxjs';


export class SearchBase {
  domain = '';
  title: string;
  profile: any = {};

  _sortMap: any[];
  _sorts: any = {};

  _facetMap: any[];
  _paramMap: any = null;
  _rows = 0;
  _start = 0;
  _searchText = '';

  _end: number;
  _page = 1;
  _pageCount: number;

  pages: number[] = [1, 2, 3, 4, 5];

  expandAll = false;
  resultNumber: number;
  searchResults: any;
  error: string;

  columns = [];
  details = {};

  searchOptions: any;

  forms: any = {};

  loading = false;

  _lastRefresh: string = null;

  canClearFilters = false;

  showDisclaimerTimer: any;

  showDisclaimer:boolean = false;

  private interval = {
    id: undefined,
    seconds: 15 * 1000
  };

  subscription: Subscription;

  bsConfig = {containerClass: 'theme-dark-blue', showWeekNumbers: false};

  facetList: string[] = [];

  get sortMap(): any[] {
    const _sortMap = this.searchService.searchParams[this.domain].sortMap;

    if (_sortMap) {
      const _sorts = {};
      _sortMap.forEach((x, index, array) => {
        _sorts[x.sortColumnName] = {
          order: x.order,
          index: array.length > 1 ? String(index + 1) : ''
        };
      });
      this._sorts = _sorts;
    }

    return _sortMap;
  }

  set sortMap(value: any[]) {
    this.searchService.searchParams[this.domain].sortMap = value;

    if (this.searchResults) {
      this.page = 1;
    }
  }

  get facetMap(): any {
    return this.searchService.searchParams[this.domain].facetMap || [];
  }

  set facetMap(value: any) {

    // filter out empty facetMap key values:
    const _facetMap = [];
    if(value) {
      value.forEach(element => {
        Object.keys(element).forEach(key => {
          if (element[key].length > 0) {
            const facetObj = {};
            facetObj[key] = element[key];
            _facetMap.push(facetObj);
          }
        });
      });
    }

    this.searchService.searchParams[this.domain].facetMap = _facetMap;
    if (this.searchResults) {
      this.page = 1;
    }

    this.canClearFilters = (
        Object.keys(this.paramMap || []).length +
        Object.keys(this.facetMap || []).length +
        this.searchText.length
      ) > 0;

  }

  get paramMap(): any {
    return this.searchService.searchParams[this.domain].paramMap || this._paramMap;
  }

  set paramMap(value: any) {
    this.searchService.searchParams[this.domain].paramMap = JSON.parse(JSON.stringify(value));
    if (this.searchResults) {
      // execute search and set page # to 1:
      this.page = 1;
    }

    this.canClearFilters = (
      Object.keys(this.paramMap || []).length +
      Object.keys(this.facetMap || []).length +
      this.searchText.length
    ) > 0;
}

  get rows(): number {
    return this.searchService.searchParams[this.domain].rows || this._rows;
  }

  set rows(value: number) {
    this.searchService.searchParams[this.domain].rows = value;
    this._rows = value;
    this.page = 1;
  }

  get searchText(): string {
    return this._searchText || '';
  }

  set searchText(value: string) {
    this._searchText = value;
  }

  get start(): number {
    return this._start;
  }

  set start(value: number) {
    this._start = value;
    if (this.searchResults) {
      this.search(this.facetMap, this.paramMap);
    }
  }

  get end(): number {
    return this._end;
  }

  set end(value: number) {
    this._end = value;
  }

  get page(): number {
    return this.searchService.searchParams[this.domain].page || this._page;
  }

  set page(value: number) {
    this.searchService.searchParams[this.domain].page = value;
    this._start = this.page * this.rows - this.rows;
    if (this.searchResults) {
      this.search(this.facetMap, this.paramMap);
    }
  }

  get pageCount(): number {
    return this._pageCount;
  }

  set pageCount(value: number) {
    this._pageCount = value;
  }

  get lastRefresh(): string {
    return this._lastRefresh;
  }

  set lastRefresh(value: string) {
    this._lastRefresh = value;
    this.startInterval();
  }

  searchTextForm = this.formFactory(['searchText']);

  constructor(
    protected searchService: SearchService,
    protected formBuilder: FormBuilder,
    protected titlecasePipe: TitleCasePipe,
    protected googleAnalyticsService: GoogleAnalyticsEventsService,
    protected route: ActivatedRoute
  ) {
    this.toggleDisclaimer(true);
  }

  protected init() {
    this.route.data.subscribe((data: {profile: any, options: any}) => {

      // set timer interval:
      this.interval.seconds = data.options.refresh.interval * 1000;
      this.startInterval();

      // set profile value for user:
      this.profile = data.profile;

      this.searchOptions = data.options;

      // use first option for rows-per-page:
      this._rows = Number(this.searchOptions.pageOptions[0].value) || 25;

      this.page = this.page;

      // execute default search:
      this.search(this.facetMap, this.paramMap);

      // update state for clear button:
      this.canClearFilters = (
        Object.keys(this.paramMap || []).length +
        Object.keys(this.facetMap || []).length +
        this.searchText.length
      ) > 0;

    });

    this.forms.dates = this.formFactory(['selectStartDate', 'selectEndDate']);
    this.forms.dates.setValidators(dateRangeValidator);
    this.forms.names = this.formFactory(['selectName', 'selectValue']);
    this.forms.identifiers = this.formFactory(['selectValue']);
    this.forms.ttabDecision = this.formFactory(['selectValue']);
    this.forms.markGS = this.formFactory(['selectName', 'selectValue']);
    this.forms.proceedingType = this.formFactory(['selectName']);
    this.forms.citablePrecedentTTAB = this.formFactory(['selectName']);

    this.googleAnalyticsService.emitEvent('Page View', 'Tab Click', this.titlecasePipe.transform(this.domain));

  }

  protected toggleDisclaimer(showDisclaimer?:boolean){
    this.showDisclaimer = showDisclaimer == null ? !this.showDisclaimer : showDisclaimer;
    clearTimeout(this.showDisclaimerTimer);
    $(window).trigger('resizeStickyTable');
    this.showDisclaimerTimer = setTimeout(() => {
      this.showDisclaimer = false;
      $(window).trigger('resizeStickyTable');
    }, 1000 * 30);
  }

  protected destroy() {
    if (this.interval.id) {
      clearInterval(this.interval.id);
    }
    this.subscription.unsubscribe();
  }

  private startInterval() {
    if (this.interval.id) {
      clearInterval(this.interval.id);
    }

    if (this.interval.seconds) {
      this.interval.id = setInterval(() => {
        this.refresh();
      }, this.interval.seconds);
    }
  }

  /**
   * Execute search based on user parameters and settings
   * @param facetMap the facet selections
   * @param paramMap the filter selections
   */
  protected search(facetMap: any = this.facetMap, paramMap: any = this.paramMap): void {
    this.loading = true;
    this.searchResults = undefined;
    this.subscription = this.searchService
      .getSearchResults(
        this.domain,
        facetMap,
        this.paramMap,
        this.rows,
        this.searchText,
        this.sortMap,
        this.start
      )
      .subscribe(
        results => {
          this.searchResults = results;
          this.error = undefined;
          this.resultNumber = results.recordTotalQuantity;

          // calculate page count from results recordTotalQuantity and page size:
          this.pageCount = Math.ceil(this.searchResults.recordTotalQuantity / this.rows);
          this.end = Math.min(
            this.start + this.rows,
            this.searchResults.results.length
          );

          // derive page numbers (block of 5) to show in navigator:
          this.pages = this.makePages(this.page, this.pageCount);
          this.loading = false;
          this.expandAll = false;
          this.lastRefresh = new Date().toLocaleTimeString();

          this.facetList = this.getFacets(
            this.searchOptions[this.domain],
            this.searchResults.aggregationData
          );
        },

        err => {
          this.pages = [];
          this.error = `Error retrieving ${this.domain} data`;
          console.error('Error retrieving ' + `${this.domain}:`, err);
          this.loading = false;
          this.expandAll = false;
        }
      );
  }

  getFacets(searchOptions: any, aggregationData: any): any[] {
    try {
      return Object

      // get facet keys from metadata:
      .keys(searchOptions.facets)

      // filter for keys contained in query:
      .filter(key => aggregationData[key] !== undefined)

      // create object with key, order:
      .map(key => ({
        key: key,
        order: searchOptions.facets[key].order || 0
      }))

      // sort by order property:
      .sort((a, b) => a.order - b.order)

      // remove order property:
      .map(item => ({key: item.key}));

    } catch (error) {
      return [];
    }
  }

  private refresh() {
    this.search(this.facetMap, this.paramMap);
  }

  /**
   * Utility method to generate intermediate pages numbers on page navigator
   * @param page the current page
   * @param pageCount the total number of pages
   */
  private makePages(page, pageCount) {
    const _pages = [];
    const _a = Math.floor((page - 1) / 5) * 5 + 1;
    const _b = Math.min(_a + 4, pageCount);
    for (let i: number = _a; i <= _b; i++) {
      _pages.push(i);
    }
    return _pages.slice(0, 5);
  }

  protected onLoadingState(loadingState: boolean) {
    this.loading = loadingState;
  }

  /**
   * Column click handler to set column sorting
   * @param col the column name which was clicked
   */
  protected onColumnClick(col) {
    // make clone of sortMap:
    let _sortMap = JSON.parse(JSON.stringify(this.sortMap || []));

    // find clicked sort column in sortMap:
    const _sort = _sortMap.filter(x => x.sortColumnName === col.sortColumnName).pop();

    // if sort column exists, and only column, toggle direction:
    if (_sort && _sortMap.length === 1) {
      _sort.order = _sort.order === 'asc' ? 'desc' : 'asc';
      this.sortMap = _sortMap;

      // it sort column exists, and more than one sort, toggle direction of clicked column:
    } else if (_sort && _sortMap.length > 1) {
      if (!col.ctrlKey) {
        _sortMap = [{ sortColumnName: col.sortColumnName, order: 'asc' }];
      } else {
        _sort.order = _sort.order === 'asc' ? 'desc' : 'asc';
      }
      this.sortMap = _sortMap;
    } else {
      if (!col.ctrlKey) {
        _sortMap = [];
      }
      if (col.sortColumnName.length > 0) {
        _sortMap.push({ sortColumnName: col.sortColumnName, order: 'asc' });
      }
      this.sortMap = _sortMap;
    }
  }

  /**
   * Toggle all detail sections open/closed
   */
  protected toggleAll() {
    if (this.searchResults) {
      if (this.expandAll === false) {
        this.searchResults.results.forEach(element => {
          element.expanded = true;
        });
        this.expandAll = true;
      } else {
        this.searchResults.results.forEach(element => {
          element.expanded = false;
        });
        this.expandAll = false;
      }
    }
  }

  /**
   * Set the rows per page from the UI control
   * @param value the new rows per page
   */
  protected setRowsPerPage(value) {
    this.rows = Number(value);
  }

  /**
   * Navigate to the next page
   * @param event the browser event dispatched
   */
  protected nextPage(event) {
    this.page = Math.min(this.page + 1, this.pageCount);
  }

  /**
   * Navigate to the previous page
   * @param event the browser event dispatched
   */
  protected previousPage(event) {
    this.page = Math.max(this.page - 1, 1);
  }

  /**
   * Navigate to the last page
   * @param event the event dispatched
   */
  protected lastPage(event) {
    this.goToPage(this.pageCount);
  }

  /**
   * Navigate to specified page
   * @param event the browser event dispatched
   * @param page the page to go to
   */
  protected goToPage(page) {
    this.page = Number(page);
  }

  /**
   * Add filter to the pramaMap
   * @param filterName the filter name
   * @param filterValue the filter value
   */
  protected addFilter(filterName: string, filterValue: any) {
    const _paramMap = JSON.parse(JSON.stringify(this.paramMap || []));
    // Avoid duplicate filters
    _paramMap.forEach((element, i) => {
      if(element[filterName]) {
        _paramMap.splice(i,1);
      }
    });
    const filterObj = {};
    filterObj[filterName] = filterValue;
    _paramMap.push(filterObj);
    this.paramMap = _paramMap;
  }

  /**
   * remove filter from the pramaMap
   * @param filterName the filter name
   */
  protected removeFilter(tag: any) {
    this.paramMap.forEach((element, i) => {
      if(element[tag.key]) {
        this.paramMap.splice(i,1);
      }
    });
    this.paramMap = this.paramMap;
    this.clearFilter(tag);
  }

  /**
   * Add facet to the facetMap
   * @param facetName the facet name
   * @param facetValue the facet value
   */
  protected addFacet(facetName: string, facetValue: any) {
    let isInserted = false;
    const _facetMap = this.facetMap;
    _facetMap.forEach(element => {
      if(element[facetName]) {
        element[facetName] = facetValue;
        isInserted = true;
      }
    });
    if(!isInserted) {
      const facetObj = {};
      facetObj[facetName] = facetValue;
      _facetMap.push(facetObj);
    }
    this.facetMap = _facetMap;
  }

  /**
   * remove facet from the facetMap
   * @param tag the facet need to delete
   */
  protected removeFacet(tag: any) {
    this.facetMap.forEach((element, index) => {
      if(tag.secondLvl < 0) {
        if(element[tag.facetName]) {
          if(element[tag.facetName].length < 2) {
            this.facetMap.splice(index, 1);
          } else {
            element[tag.facetName].forEach((valueEl, valIndx) => {
              if(valueEl.value === tag.label) {
                element[tag.facetName].splice(tag.firstLvl, 1);
              }
            });
          }
        }
      } else if (element[tag.facetName] && element[tag.facetName][tag.firstLvl].nestedFacetBag.length < 2) {
        element[tag.facetName].forEach((valueEl, valIndx) => {
          valueEl.nestedFacetBag.forEach((key, keyIndex) => {
            if(valueEl.value +": "+key  === tag.label) {
              element[tag.facetName].splice(tag.firstLvl, 1);
              valueEl.nestedFacetBag.splice(keyIndex, 1);
            }
          });
        });
      } else if (element[tag.facetName] && element[tag.facetName][tag.firstLvl].nestedFacetBag.length <= 2) {
        element[tag.facetName][tag.firstLvl].nestedFacetBag.forEach((valueEl, valIndx) => {
          element[tag.facetName][tag.firstLvl].nestedFacetBag.splice(tag.secondLvl, 1);
        });
      }
    });
    this.facetMap = this.facetMap;
  }

  protected clearFilter(tag: any) {
    Object.keys(this.forms).forEach(key => {
      const form: FormGroup = this.forms[key] as FormGroup;
      this.searchOptions[this.domain].filters[key].items.forEach(item => {
        if(item.name === tag.key) {
          Object.keys(form.controls).forEach(ctrl => {
            (form.get(ctrl) as FormControl).setValue(null);
          });
        }
      });
    });
  }

  /**
   * clear filter from the paramMap
   */
  protected clearFilters(query = false) {
    this.searchResults = undefined;
    this.paramMap = [];
    this.sortMap = [];
    this.facetMap = [];
    this._rows = Number(this.searchOptions.pageOptions[0].value) || 25;
    this.start = 0;
    this.page = 1;

    // clear form fields:
    Object.keys(this.forms).forEach(key => {
      const form: FormGroup = this.forms[key] as FormGroup;
      Object.keys(form.controls).forEach(ctrl => {
        (form.get(ctrl) as FormControl).setValue(null);
      });
    });

    if (query) {
      this.searchText = "";
      this.searchTextForm.controls["searchText"].setValue("");
      // update state for clear button:
      this.canClearFilters = (
        Object.keys(this.paramMap || []).length +
        Object.keys(this.facetMap || []).length +
        this.searchText.length
      ) > 0;
      this.search([], []);
    }

  }

  protected formFactory(controls: string[]): FormGroup {
    const group: any = {};
    controls.forEach((control: string) => {
      group[control] = this.formBuilder.control(null, Validators.required);
    });
    return this.formBuilder.group(group);
  }

  protected facetChangedHandler(change: any) {
    this.addFacet(change.name, change.value);
  }

  /**
   * Determines if facets should be displayed
   * Show if searchResults exists and contains more than 1 row or at least one facet selection
   * @param searchResults the searchResults
   * @param facetMap the facetMap
   */
  protected showFacets(searchResults, facetMap) {
    return (
      searchResults && (searchResults.results.length > 0 || Object.keys(facetMap).length > 0)
    );
  }

  searchByDates(form: FormGroup) {
    const dateObj = {
      from: this.searchService.getFormattedDate(form.get('selectStartDate').value),
      to: this.searchService.getFormattedDate(form.get('selectEndDate').value)
    };
    this.addFilter('decisionDate', dateObj);
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Date Range Filter`,
      'Button Click', dateObj.from+' to '+dateObj.to);
  }

  searchByNames(form: FormGroup) {
    this.addFilter(
      form.get('selectName').value.split('|')[0],
      form.get('selectValue').value
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Name Filter`,
      'Button Click', form.get('selectName').value.split('|')[1]);
  }

  searchByIdentifiers(form: FormGroup) {
    this.addFilter(
      'proceedingNumber',
      form.get('selectValue').value
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Proceeding or Application number Filter`,
      'Button Click', form.get('selectValue').value);
  }

  searchByTTABDecision(form: FormGroup) {
    this.addFilter(
      'decision',
      form.get('selectValue').value
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} TTAB Decision Filter`,
      'Button Click', form.get('selectValue').value);
  }

  searchByMarkGS(form: FormGroup) {
    this.addFilter(
      form.get('selectName').value.split('|')[0],
      form.get('selectValue').value
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Mark and goods or services Filter`,
      'Button Click', form.get('selectName').value.split('|')[1]);
  }

  searchByProceedingType(form: FormGroup) {
    this.addFilter(
      'proceedingTypeCd',
      form.get('selectName').value.split('|')[0]
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Type Of Case Filter`,
      'Button Click', form.get('selectName').value.split('|')[1]);
  }

  searchByCitablePrecedentTTAB(form: FormGroup) {
    this.addFilter(
      'precedentCitableIndicator',
      form.get('selectName').value.split('|')[0]
    );
    this.googleAnalyticsService.emitEvent(`${this.titlecasePipe.transform(this.domain)} Citable as precedent of TTAB Filter`,
      'Button Click', form.get('selectName').value.split('|')[1]);
  }

  activeFilters(array: any[]): any[] {
    return array.filter((item: any) => (item.active === undefined) ? true : item.active);
  }

  logClear() {
    this.googleAnalyticsService.emitEvent('Clear Refine By', 'Link Click',
      `Clear ${this.titlecasePipe.transform(this.domain)} Refiners`);
  }

}
